import React from 'react';
import './selection-action-bar.scss';

export interface ActionButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

interface SelectionActionBarProps {
  selectedCount: number;
  itemLabel?: string; // e.g., "invoice", "project" - will be pluralized automatically
  onClearSelection: () => void;
  actions: ActionButton[];
  isVisible?: boolean;
  // Selection expansion options
  totalFilteredCount?: number;
  onSelectPage?: () => void;
  onSelectAll?: () => void;
  currentPageCount?: number;
  isAllSelected?: boolean;
}

const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  selectedCount,
  itemLabel = 'item',
  onClearSelection,
  actions,
  isVisible = true,
  totalFilteredCount,
  onSelectPage,
  onSelectAll,
  currentPageCount,
  isAllSelected = false
}) => {
  if (!isVisible || selectedCount === 0) {
    return null;
  }

  const pluralizedLabel = selectedCount === 1 ? itemLabel : `${itemLabel}s`;
  const showSelectPage = onSelectPage && currentPageCount && selectedCount < currentPageCount;
  const showSelectAll = onSelectAll && totalFilteredCount && selectedCount < totalFilteredCount && !isAllSelected;

  return (
    <div className="selection-action-bar">
      <div className="selection-action-bar__content">
        <div className="selection-action-bar__left">
          <div className="selection-action-bar__info">
            <span className="selection-action-bar__checkmark">✓</span>
            <span className="selection-action-bar__count">
              {isAllSelected ? `All ${totalFilteredCount?.toLocaleString()}` : selectedCount.toLocaleString()} {pluralizedLabel} selected
            </span>
          </div>

          {/* Selection expansion buttons */}
          {(showSelectPage || showSelectAll) && (
            <div className="selection-action-bar__expand">
              {showSelectPage && (
                <button
                  type="button"
                  className="selection-action-bar__text-btn"
                  onClick={onSelectPage}
                >
                  Select Page ({currentPageCount})
                </button>
              )}
              {showSelectPage && showSelectAll && (
                <span className="selection-action-bar__separator">|</span>
              )}
              {showSelectAll && (
                <button
                  type="button"
                  className="selection-action-bar__text-btn"
                  onClick={onSelectAll}
                >
                  Select All ({totalFilteredCount?.toLocaleString()})
                </button>
              )}
            </div>
          )}
        </div>

        <div className="selection-action-bar__actions">
          <button
            type="button"
            className="selection-action-bar__btn selection-action-bar__btn--clear"
            onClick={onClearSelection}
          >
            Clear Selection
          </button>

          {actions.map((action, index) => (
            <button
              key={index}
              type="button"
              className={`selection-action-bar__btn selection-action-bar__btn--${action.variant || 'primary'}`}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectionActionBar;
